import { Check, Circle, Loader2 } from 'lucide-react';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ExecutionPlanStep } from '@/services/api/agentflowService';

export interface StepTimelineProps {
  /** Steps from execution plan (includes is_current flag) */
  steps: ExecutionPlanStep[];
  /** If true, render a more compact horizontal layout */
  compact?: boolean;
}

const getStepTypeLabel = (stepType: string): string => {
  switch (stepType) {
    case 'voice_ai_call':
      return 'Voice AI Call';
    case 'dialpad_call':
      return 'RingCentral Call';
    case 'email_follow_up':
      return 'Email';
    case 'text_claimant':
      return 'Text';
    case 'bpo_call':
      return 'BPO Call';
    default:
      return stepType.replace(/_/g, ' ');
  }
};

export const StepTimeline: React.FC<StepTimelineProps> = ({
  steps,
  compact = false,
}) => {
  if (!steps || steps.length === 0) {
    return (
      <div className="text-xs text-zinc-500 py-2">No steps in execution plan</div>
    );
  }

  // Find the current step index
  const currentIndex = steps.findIndex((s) => s.is_current);

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'flex items-center',
          compact ? 'gap-1' : 'gap-2 flex-wrap'
        )}
      >
        {steps.map((step, index) => {
          // Determine status relative to current
          const isCompleted = currentIndex >= 0 && index < currentIndex;
          const isCurrent = step.is_current;
          const isUpcoming = currentIndex < 0 || index > currentIndex;

          return (
            <React.Fragment key={step.step_id}>
              {index > 0 && (
                <div
                  className={cn(
                    'h-0.5',
                    compact ? 'w-2' : 'w-4',
                    isCompleted
                      ? 'bg-emerald-400'
                      : isCurrent
                        ? 'bg-violet-300'
                        : 'bg-zinc-200'
                  )}
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center justify-center rounded-full transition-colors',
                      compact ? 'h-5 w-5' : 'h-6 w-6',
                      isCompleted && 'bg-emerald-500 text-white',
                      isCurrent && 'bg-violet-600 text-white ring-2 ring-violet-200',
                      isUpcoming && 'bg-zinc-100 text-zinc-400 border border-zinc-300'
                    )}
                  >
                    {isCompleted ? (
                      <Check className={cn(compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
                    ) : isCurrent ? (
                      <Loader2
                        className={cn(
                          'animate-spin',
                          compact ? 'h-3 w-3' : 'h-3.5 w-3.5'
                        )}
                      />
                    ) : (
                      <Circle className={cn(compact ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px]">
                  <p className="font-medium text-xs">
                    {step.label || getStepTypeLabel(step.step_type)}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    {getStepTypeLabel(step.step_type)}
                    {step.delay_days > 0 && ` · +${step.delay_days}d delay`}
                  </p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    {isCompleted
                      ? 'Completed'
                      : isCurrent
                        ? 'In Progress'
                        : 'Upcoming'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default StepTimeline;
